import { useEffect, useState } from "react";
import { ApiClientError, fetchSummary, fetchGcr } from "../api/client";
import { StateBanner } from "../components/StateBanner";
import { PeriodSwitcher } from "../components/PeriodSwitcher";
import { BarChart } from "../components/BarChart";
import { GcrCard } from "../components/GcrCard";
import { Badge } from "../components/Badge";
import { channelMeta, CHANNEL_STATE_META, yen } from "../lib/meta";
import { usePeriod } from "../lib/usePeriod";
import type { GcrResponse, SummaryChannel, SummaryResponse } from "../types/contract";

type FetchState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ok"; data: SummaryResponse };

/**
 * 実売上を持つチャネルかどうか。表示レイアウトの出し分けに使う。
 * shopify/rakuten は常に売上レイアウト。amazon は worker が revenue を数値で返したとき（2026-11-02〜・D1 orders 由来）だけ
 * 売上レイアウト。revenue が null（旧 worker・売上未連携）の間は従来の広告計測レイアウトに「売上: 未連携」を出す。
 */
function isSalesChannel(ch: SummaryChannel): boolean {
  if (ch.channel === "amazon") return ch.revenue !== null && ch.revenue !== undefined;
  return ch.channel === "shopify" || ch.channel === "rakuten";
}

export function DashboardPage() {
  const { period, customStart, customEnd, range, setPeriod, setCustomStart, setCustomEnd } = usePeriod();
  const [state, setState] = useState<FetchState>({ status: "loading" });
  // GCR 小カード（2026-10-19）。summary とは独立に取り、失敗しても summary の表示は止めない（worker 未デプロイでも落ちない）。
  const [gcr, setGcr] = useState<GcrResponse | null>(null);
  const [gcrTick, setGcrTick] = useState(0);
  useEffect(() => {
    let cancelled = false;
    fetchGcr(range.from, range.to)
      .then((d) => {
        if (!cancelled) setGcr(d);
      })
      .catch(() => {
        if (!cancelled) setGcr(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, gcrTick]);

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    fetchSummary(range.from, range.to)
      .then((data) => {
        if (!cancelled) setState({ status: "ok", data });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const message = e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。";
        setState({ status: "error", message });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to]);

  const retry = () => {
    setState({ status: "loading" });
    fetchSummary(range.from, range.to)
      .then((data) => setState({ status: "ok", data }))
      .catch((e: unknown) => {
        const message = e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。";
        setState({ status: "error", message });
      });
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">ダッシュボード</h1>
        <PeriodSwitcher
          period={period}
          onChange={setPeriod}
          customStart={customStart}
          customEnd={customEnd}
          onCustomStartChange={setCustomStart}
          onCustomEndChange={setCustomEnd}
        />
      </div>

      {state.status === "loading" && <SkeletonCards />}
      {state.status === "error" && <StateBanner kind="error" message={state.message} onRetry={retry} />}
      {state.status === "ok" && (
        <>
          {state.data.sources.missing_dates.length > 0 && (
            <StateBanner
              kind="missing"
              message={`${state.data.sources.missing_dates.join(", ")} の日次データが未取得です（最新取得日: ${
                state.data.sources.daily_json_latest ?? "—"
              }）`}
            />
          )}
          <div className="card-grid">
            {state.data.channels.map((ch) => (
              <ChannelCard key={ch.channel} ch={ch} />
            ))}
          </div>

          {gcr && <GcrCard data={gcr} onChanged={() => setGcrTick((t) => t + 1)} />}

          <div className="card">
            <h2 className="section-title">週次支出・売上推移</h2>
            <BarChart
              data={state.data.weekly.map((w) => ({ label: w.week, value: w.spend }))}
              series2={state.data.weekly.map((w) => ({ label: w.week, value: w.revenue }))}
              color="var(--accent)"
              color2="var(--ch-shopify-c)"
              legend={[
                { label: "広告費（Meta＋Google＋Amazon）", color: "var(--accent)" },
                { label: "売上（Shopify+楽天+Amazon）", color: "var(--ch-shopify-c)" },
              ]}
            />
          </div>
        </>
      )}
    </div>
  );
}

/**
 * 広告費の媒体内訳（2026-10-17・Google 広告の合流、2026-10-23・Amazon 広告の合流）。worker が spend_breakdown を返し、
 * かつ Meta 以外の媒体が1つでも取れている（null でない）ときだけ出す。旧 worker（フィールド無し）では何も出さない。
 */
function SpendBreakdown({ ch }: { ch: SummaryChannel }) {
  const b = ch.spend_breakdown;
  if (!b) return null;
  // 2026-11-04: Amazon カードは「Amazon広告（Amazon 内）」と「Meta→Amazon（外部流入）」の 2 本立て。Meta 分が 0 の期間は並べない
  if (ch.channel === "amazon") {
    const parts: string[] = [];
    if (b.amazon !== null && b.amazon !== undefined) parts.push(`Amazon広告 ${yen(b.amazon)}`);
    if ((b.meta ?? 0) > 0) parts.push(`Meta→Amazon ${yen(b.meta as number)}`);
    if (parts.length < 2) return null;
    return <div className="ad-note">内訳: {parts.join(" ・ ")}</div>;
  }
  const parts: string[] = [];
  if (b.meta !== null && b.meta !== undefined) parts.push(`Meta ${yen(b.meta)}`);
  if (b.rpp !== null && b.rpp !== undefined) parts.push(`RPP ${yen(b.rpp)}`);
  if (b.google !== null && b.google !== undefined) parts.push(`Google ${yen(b.google)}`);
  // Amazon は広告費 >0 のときだけ内訳に出す（Amazon 以外のカードに ¥0 を並べない・契約 §7）
  const hasAmazon = (b.amazon ?? 0) > 0;
  if (hasAmazon) parts.push(`Amazon ${yen(b.amazon as number)}`);
  const hasNonMeta = (b.rpp !== null && b.rpp !== undefined) || (b.google !== null && b.google !== undefined) || hasAmazon;
  if (!hasNonMeta || parts.length < 2) return null;
  return <div className="ad-note">内訳: {parts.join(" ・ ")}</div>;
}

/**
 * Meta→Amazon（2026-11-04）: Meta のキャンペーン名に amazon を含む分（STOQ_AmazonLP_2611 → LP /amazon/ → Amazon）の広告費と、
 * 追跡ID（META-2611B・コード STOQ14）が付いた Amazon 注文の件数・売上・ROAS・CPA。Amazon 広告（Amazon 内）と並べて見る。
 * 旧 worker（フィールド無し）・両方読めない（spend/orders とも null）ときは何も出さない。
 */
function MetaAmazonRows({ ch }: { ch: SummaryChannel }) {
  const m = ch.meta_amazon;
  if (ch.channel !== "amazon" || !m) return null;
  if (m.spend === null && m.orders === null) return null;
  const ids = m.promotion_ids.length > 0 ? m.promotion_ids.join("・") : "META-2611B";
  return (
    <div className="sub-block">
      <div className="sub-block-title">Meta→Amazon（外部流入・コード経由）</div>
      <div className="metric-row">
        <span>Meta広告費</span>
        <b>{m.spend !== null ? yen(m.spend) : "—"}</b>
      </div>
      <div className="metric-row">
        <span>コード注文</span>
        <b>{m.orders !== null ? `${m.orders.toLocaleString("ja-JP")}件` : "—"}</b>
      </div>
      <div className="metric-row">
        <span>コード売上</span>
        <b>{m.revenue !== null ? yen(m.revenue) : "—"}</b>
      </div>
      <div className="metric-row">
        <span>ROAS</span>
        <b>{m.roas !== null ? `${m.roas.toFixed(2)}x` : "—"}</b>
      </div>
      <div className="metric-row">
        <span>CPA</span>
        <b>{m.cpa !== null ? yen(m.cpa) : "—"}</b>
      </div>
      {m.amazon_clicks !== null && (
        <div className="metric-row">
          <span>LP→Amazonクリック</span>
          <b>{m.amazon_clicks.toLocaleString("ja-JP")}件</b>
        </div>
      )}
      <div className="ad-note">
        追跡ID {ids} の注文（キャンセル除く
        {m.pending_orders ? `・${m.pending_orders}件は金額確定待ち` : ""}）。Meta 費は毎時更新・確定は翌朝 7 時台
      </div>
    </div>
  );
}

/**
 * Amazon の当日広告費「見込み」（2026-11-02）。確定の広告費（amazon_ads_spend_daily）は翌朝 JST 7 時台まで当日分が
 * 入らないため、worker は期間に今日を含むときだけ amazon_today（予算消化率 × 日予算）を付ける。確定 spend とは
 * 別の行として出し、合算しない（二重計上しない）。旧 worker / 期間外 / 取得失敗（null）は何も出さない。
 */
function AmazonTodayRow({ ch }: { ch: SummaryChannel }) {
  const t = ch.amazon_today;
  if (ch.channel !== "amazon" || !t) return null;
  const hhmm = new Date(t.as_of).toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit" });
  return (
    <>
      <div className="metric-row">
        <span>本日見込み</span>
        <b>{yen(t.spend_estimate)}</b>
      </div>
      <div className="ad-note">
        {hhmm} 時点の予算消化率 × 日予算（{t.campaigns}本
        {t.campaigns_unknown > 0 ? `・${t.campaigns_unknown}本は消化率未取得` : ""}）。確定額は翌朝 7 時台に反映
      </div>
    </>
  );
}

function ChannelCard({ ch }: { ch: SummaryChannel }) {
  const meta = channelMeta(ch.channel);
  const stateBadge = ch.state !== "ok" ? CHANNEL_STATE_META[ch.state] : null;

  return (
    <div className="channel-card">
      <div className="ch-label">
        <span className="dot" style={{ background: meta.c }} />
        {ch.label}
      </div>

      {ch.state === "no_data" ? (
        <div className="state-badge-row">
          <Badge label={stateBadge!.label} color={stateBadge!.c} bg={stateBadge!.bg} />
        </div>
      ) : isSalesChannel(ch) ? (
        <>
          <div className="spend">{ch.revenue !== null ? yen(ch.revenue) : "—"}</div>
          <div className="metric-row">
            <span>注文数</span>
            <b>{ch.orders !== null ? `${ch.orders.toLocaleString("ja-JP")}件` : "—"}</b>
          </div>
          {ch.spend !== null && ch.spend > 0 ? (
            <>
              <div className="metric-row">
                <span>広告費</span>
                <b>{yen(ch.spend)}</b>
              </div>
              <div className="metric-row">
                <span>ROAS</span>
                <b>{ch.roas !== null ? `${ch.roas.toFixed(2)}x` : "—"}</b>
              </div>
              <SpendBreakdown ch={ch} />
            </>
          ) : (
            <div className="metric-row faint">
              <span>広告費</span>
              <b>{ch.spend === null ? "未連携" : "¥0"}</b>
            </div>
          )}
          {ch.ad_note && <div className="ad-note">※{ch.ad_note}</div>}
          <AmazonTodayRow ch={ch} />
          <MetaAmazonRows ch={ch} />
          {stateBadge && (
            <div className="state-badge-row">
              <Badge label={stateBadge.label} color={stateBadge.c} bg={stateBadge.bg} />
            </div>
          )}
        </>
      ) : (
        <>
          <div className="spend">{ch.spend !== null ? yen(ch.spend) : "—"}</div>
          <div className="metric-row">
            <span>{ch.primary.label}</span>
            <b>{ch.primary.value ?? "—"}</b>
          </div>
          <div className="metric-row">
            <span>{ch.sub.label}</span>
            <b>{ch.sub.value ?? "—"}</b>
          </div>
          {/* Amazon の売上が未取得（revenue=null）の間だけ「未連携」と明示する。数値があれば上の売上レイアウトで描く */}
          {ch.channel === "amazon" && ch.revenue === null && (
            <div className="metric-row faint">
              <span>売上</span>
              <b>未連携</b>
            </div>
          )}
          <AmazonTodayRow ch={ch} />
          <MetaAmazonRows ch={ch} />
          {ch.spend_breakdown &&
            ((ch.spend_breakdown.google ?? 0) > 0 || (ch.spend_breakdown.amazon ?? 0) > 0) && <SpendBreakdown ch={ch} />}
          {stateBadge && (
            <div className="state-badge-row">
              <Badge label={stateBadge.label} color={stateBadge.c} bg={stateBadge.bg} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SkeletonCards() {
  return (
    <div className="card-grid">
      {[0, 1, 2, 3].map((i) => (
        <div className="channel-card" key={i}>
          <div className="skeleton" style={{ height: 14, width: "60%", marginBottom: 12 }} />
          <div className="skeleton" style={{ height: 24, width: "80%", marginBottom: 10 }} />
          <div className="skeleton" style={{ height: 12, width: "50%" }} />
        </div>
      ))}
    </div>
  );
}
